import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  IconButton,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../services/api";

interface Employee {
  id: number;
  name: string;
  employee_code: string;
}

interface LeaveType {
  id: number;
  name: string;
}

interface ApprovalEntry {
  approver_id: string;
  action: string;
  comments: string;
  acted_at: string;
}

const APPROVAL_ACTIONS = [
  "recommend",
  "approve",
  "reject",
  "return",
  "forward",
];

const emptyApprovalEntry = (): ApprovalEntry => ({
  approver_id: "",
  action: "approve",
  comments: "",
  acted_at: "",
});

const ManualLeaveEntryManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState({
    employee_id: "",
    leave_type_id: "",
    start_date: "",
    end_date: "",
    number_of_days: "",
    reason: "",
  });

  const [approvalEntries, setApprovalEntries] = useState<ApprovalEntry[]>([
    emptyApprovalEntry(),
  ]);

  useEffect(() => {
    (async () => {
      try {
        const [employeesRes, leaveTypesRes] = await Promise.all([
          api.get("/admin/employees").catch(() => ({ data: [] })),
          api.get("/admin/leave-types").catch(() => ({ data: [] })),
        ]);
        setEmployees(employeesRes.data || []);
        setLeaveTypes(leaveTypesRes.data || []);
      } catch {
        // Non-fatal: dropdowns will just be empty
      }
    })();
  }, []);

  const handleApprovalEntryChange = (
    index: number,
    field: keyof ApprovalEntry,
    value: string,
  ) => {
    setApprovalEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const addApprovalEntry = () =>
    setApprovalEntries((prev) => [...prev, emptyApprovalEntry()]);

  const removeApprovalEntry = (index: number) =>
    setApprovalEntries((prev) => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setForm({
      employee_id: "",
      leave_type_id: "",
      start_date: "",
      end_date: "",
      number_of_days: "",
      reason: "",
    });
    setApprovalEntries([emptyApprovalEntry()]);
  };

  const handleSubmit = async () => {
    setError(null);
    if (
      !form.employee_id ||
      !form.leave_type_id ||
      !form.start_date ||
      !form.end_date ||
      !form.number_of_days
    ) {
      setError("Employee, leave type, dates and number of days are required");
      return;
    }

    const validEntries = approvalEntries.filter(
      (entry) => entry.approver_id && entry.action,
    );

    setSubmitting(true);
    try {
      await api.post("/admin/leaves/manual-entry", {
        employee_id: Number(form.employee_id),
        leave_type_id: Number(form.leave_type_id),
        start_date: form.start_date,
        end_date: form.end_date,
        number_of_days: Number(form.number_of_days),
        reason: form.reason,
        approval_entries: validEntries.map((entry) => ({
          approver_id: Number(entry.approver_id),
          action: entry.action,
          comments: entry.comments,
          acted_at: entry.acted_at || undefined,
        })),
      });
      setSuccess("Leave entry recorded successfully");
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to record leave entry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Manually Record a Missed Leave
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: "block", mb: 2 }}
      >
        Use this to back-fill a leave for staff who took leave but never applied
        through the system. The entry is created as approved, deducted from the
        employee's balance, and can include the approval/comment trail below for
        record-keeping.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Employee"
              value={form.employee_id}
              onChange={(e) =>
                setForm({ ...form, employee_id: e.target.value })
              }
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name} ({emp.employee_code})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Leave Type"
              value={form.leave_type_id}
              onChange={(e) =>
                setForm({ ...form, leave_type_id: e.target.value })
              }
            >
              {leaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Number of Days"
              value={form.number_of_days}
              onChange={(e) =>
                setForm({ ...form, number_of_days: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Approval / Comment History (optional)
        </Typography>
        {approvalEntries.map((entry, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Approver"
                value={entry.approver_id}
                onChange={(e) =>
                  handleApprovalEntryChange(
                    index,
                    "approver_id",
                    e.target.value,
                  )
                }
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Action"
                value={entry.action}
                onChange={(e) =>
                  handleApprovalEntryChange(index, "action", e.target.value)
                }
              >
                {APPROVAL_ACTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={entry.acted_at}
                onChange={(e) =>
                  handleApprovalEntryChange(index, "acted_at", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Comments"
                value={entry.comments}
                onChange={(e) =>
                  handleApprovalEntryChange(index, "comments", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton
                onClick={() => removeApprovalEntry(index)}
                disabled={approvalEntries.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={addApprovalEntry}
          size="small"
          sx={{ mb: 2 }}
        >
          Add Approval Step
        </Button>

        <Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Record Leave"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManualLeaveEntryManagement;
